import { Box } from '@mui/material'
import { useIntl } from 'react-intl'
import type { SettingsFlow } from '@ory/client'

import { tokens } from '@configuration/theme'
import Button from '@base/Button/Button.component'
import Card from '@base/Card/Card.component'
import Select from '@base/Select/Select.component'
import TextField from '@base/TextField/TextField.component'
import { SUPPORTED_LOCALES } from '@i18n/locales.constants'
import { findFieldError, findInput, nodesForGroup } from '@utils/kratos-nodes.utils'

import { writeSubmittedContext } from '../../SettingsPage.utils'

interface Props {
  flow: SettingsFlow
  csrfValue: string
}

// `method=profile` is a full traits replacement, not a JSON patch — every
// schema-required field must be in the POST or Kratos rejects with 4000002.
// Email is required by the schema but is changed only via Kratos's separate
// verification flow; here it rides along as a hidden field.
const FIRST_NAME = 'traits.name.first'
const LAST_NAME = 'traits.name.last'
const LOCALE = 'traits.locale'
const EMAIL = 'traits.email'

const hasStringEmail = (traits: unknown): traits is { email: string } =>
  traits !== null &&
  typeof traits === 'object' &&
  'email' in traits &&
  typeof (traits as { email: unknown }).email === 'string'

const readIdentityEmail = (identity: SettingsFlow['identity']): string => {
  const traits = identity?.traits
  return hasStringEmail(traits) ? traits.email : ''
}

const ProfilePanel = ({ flow, csrfValue }: Props) => {
  const intl = useIntl()

  const nodes = nodesForGroup(flow.ui.nodes, 'profile')

  const firstAttrs = findInput(nodes, FIRST_NAME)
  const lastAttrs = findInput(nodes, LAST_NAME)
  const localeAttrs = findInput(nodes, LOCALE)
  const emailAttrs = findInput(nodes, EMAIL)

  // Node value preserves input across error rebuilds; identity is the fallback.
  const emailValue =
    (typeof emailAttrs?.value === 'string' && emailAttrs.value !== '' ? emailAttrs.value : null) ??
    readIdentityEmail(flow.identity)

  const firstError = findFieldError(nodes, FIRST_NAME, intl)
  const lastError = findFieldError(nodes, LAST_NAME, intl)
  const localeError = findFieldError(nodes, LOCALE, intl)

  const firstValue = String(firstAttrs?.value ?? '')
  const lastValue = String(lastAttrs?.value ?? '')
  const currentLocale = String(localeAttrs?.value ?? '')
  const isKnownLocale = SUPPORTED_LOCALES.some((l) => l.short === currentLocale)

  // Schema may drop fields between releases — render only what Kratos emits
  // instead of POSTing empty traits that the server would silently accept.
  if (!firstAttrs && !lastAttrs && !localeAttrs) {
    return (
      <Card>
        <Box sx={{ color: tokens.businessBlack60 }}>
          {intl.formatMessage({ id: 'settings.profile.unavailable' })}
        </Box>
      </Card>
    )
  }

  // Sync handler — writes sessionStorage so the success UI on the next mount
  // knows which tab triggered 1050001. MUST stay synchronous: switching to
  // AJAX submit or wrapping the write in a promise breaks the banner +
  // password-tab sign-out CTA after the Kratos 303 round-trip.
  //
  // A name field that already has a value is rendered `required` (below), so
  // the browser blocks clearing it before this fires — no confirm dialog
  // needed. Clearing a name would otherwise wipe it from every paretos surface
  // (cockpit listings, audit, team-management) at once.
  const handleSubmit = (): void => {
    writeSubmittedContext({ flowId: flow.id, tab: 'profile' })
  }

  return (
    <Box
      component='form'
      data-testid='profile-panel'
      action={flow.ui.action}
      method={flow.ui.method}
      onSubmit={handleSubmit}
      sx={{ width: '100%', minHeight: '360px' }}
    >
      <input type='hidden' name='csrf_token' value={csrfValue} />
      {/* Hidden discriminator — kept off the submit button so it survives any
       * programmatic submit and isn't dropped as a button name/value. */}
      <input type='hidden' name='method' value='profile' />
      {/* Omitted entirely when empty — value="" would hit Kratos's format
       * error instead of the cleaner "missing" path, with no field to fix. */}
      {emailValue !== '' && <input type='hidden' name={EMAIL} value={emailValue} />}

      <Card
        title={intl.formatMessage({ id: 'settings.profile.section.title' })}
        description={intl.formatMessage({ id: 'settings.profile.section.description' })}
      >
        <Box
          sx={{
            display: 'grid',
            // `minmax(0, 1fr)` not `1fr`: a bare `1fr` track keeps an implicit
            // `min-width: auto` (min-content), so a long value (e.g. an email)
            // refuses to shrink and overflows the card on narrow screens.
            gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) minmax(0, 1fr)' },
            gap: '18px',
          }}
        >
          {firstAttrs && (
            <TextField
              id='settings-first-name'
              name={FIRST_NAME}
              type='text'
              label={intl.formatMessage({ id: 'settings.profile.firstName.label' })}
              defaultValue={firstValue}
              autoComplete='given-name'
              required={firstValue !== ''}
              error={firstError}
            />
          )}
          {lastAttrs && (
            <TextField
              id='settings-last-name'
              name={LAST_NAME}
              type='text'
              label={intl.formatMessage({ id: 'settings.profile.lastName.label' })}
              defaultValue={lastValue}
              autoComplete='family-name'
              required={lastValue !== ''}
              error={lastError}
            />
          )}
          {localeAttrs && (
            // Span the full row when there's no email beside it.
            <Box sx={{ minWidth: 0, gridColumn: emailValue !== '' ? 'auto' : '1 / -1' }}>
              <Select
                id='settings-locale'
                name={LOCALE}
                label={intl.formatMessage({ id: 'settings.profile.locale.label' })}
                defaultValue={currentLocale}
                error={localeError}
              >
                {/* Disabled option preserves an out-of-list server value rather
                 * than silently bumping the user to the first SUPPORTED_LOCALES entry. */}
                {!isKnownLocale && (
                  <option value={currentLocale} disabled={true}>
                    {currentLocale || '—'}
                  </option>
                )}
                {SUPPORTED_LOCALES.map((l) => (
                  <option key={l.short} value={l.short}>
                    {intl.formatMessage({ id: l.labelKey })}
                  </option>
                ))}
              </Select>
            </Box>
          )}
          {/* Read-only display of the signed-in email. Email is edited only via
           * Kratos's separate verification flow, so this field carries no `name`
           * and is never submitted — the hidden `traits.email` input above is
           * what rides along in the profile POST. */}
          {emailValue !== '' && (
            // Span the full row when there's no locale beside it.
            <Box sx={{ minWidth: 0, gridColumn: localeAttrs ? 'auto' : '1 / -1' }}>
              <TextField
                id='settings-email'
                type='email'
                label={intl.formatMessage({ id: 'settings.profile.email.label' })}
                value={emailValue}
                readOnly={true}
                autoComplete='email'
              />
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1.5,
            mt: 3,
            pt: '20px',
            borderTop: `1px solid ${tokens.smokeySilver}`,
          }}
        >
          <Button variant='primary' size='md' type='submit'>
            {intl.formatMessage({ id: 'settings.profile.submit' })}
          </Button>
        </Box>
      </Card>
    </Box>
  )
}

export default ProfilePanel
